import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from "react-toastify";


const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const years = Array.from({ length: 21 }, (_, i) => (new Date().getFullYear() - 20 + i).toString());

const ExperienceModal = ({ open, onClose, onSave }) => {
  const [experience, setExperience] = useState({
    job_title: '',
    organization: '',
    startMonth: '',
    startYear: '',
    endMonth: '',
    endYear: '',
    current: false
  });

  const [errors, setErrors] = useState({
    job_title: false,
    organization: false,
    startDate: false,
    endDate: false
  });

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    const isJobTitleEmpty = !experience.job_title.trim();
    const isOrganizationEmpty = !experience.organization.trim();
    const isStartDateEmpty = !experience.startMonth || !experience.startYear;
    const isEndDateEmpty = !experience.current && (!experience.endMonth || !experience.endYear);

    setIsSaveDisabled(isJobTitleEmpty || isOrganizationEmpty || isStartDateEmpty || isEndDateEmpty);
  }, [experience]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setExperience((prev) => ({
      ...prev,
      [name]: value
    }));
    validateField(name, value);
  };



  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setExperience((prev) => ({
      ...prev,
      current: checked,
      endMonth: checked ? '' : prev.endMonth,
      endYear: checked ? '' : prev.endYear,
    }));

    // Revalidate only the date fields if current job is checked
    validateField("endDate", checked ? null : experience);
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;

    setExperience((prev) => {
      const updatedExperience = { ...prev, [name]: value };

      // Only validate if the end date or start date is being changed
      if (name === "endMonth" || name === "endYear" || name === "startMonth" || name === "startYear") {
        validateField("endDate", updatedExperience);
      }
      return updatedExperience;
    });
  };

  const validateField = (name, updatedExperience) => {
    let newErrors = { ...errors };
    const currentExperience = updatedExperience || experience;

    if (name === "endDate") {
      if (currentExperience.startMonth && currentExperience.startYear && !currentExperience.current && currentExperience.endMonth && currentExperience.endYear) {
        const startDate = new Date(`${currentExperience.startYear}-${months.indexOf(currentExperience.startMonth) + 1}-01`);
        const endDate = currentExperience.current
          ? new Date()
          : new Date(`${currentExperience.endYear}-${months.indexOf(currentExperience.endMonth) + 1}-01`);
        newErrors.endDate = endDate <= startDate;
        newErrors.startDate = false; // Reset start date error since we don't validate it directly here
      } else if (currentExperience.startMonth && currentExperience.startYear && !currentExperience.current && (!currentExperience.endMonth || !currentExperience.endYear)) {
        newErrors.endDate = false; // Don't show error if end date is empty and not current
      } else if (currentExperience.startMonth && currentExperience.startYear && currentExperience.current) {
        newErrors.endDate = false; // Don't show error if current is checked
      } else {
        newErrors.endDate = false;
      }
    }
    setErrors(newErrors);
  };


  const validateJobDates = (data) => {
    const today = new Date();

    // Parse the starting and end dates from the object
    const startingDate = new Date(data.starting);
    const endDate = data.end ? new Date(data.end) : null;

    // Check if the starting date is in the future
    if (startingDate > today) {
      return {
        valid: false,
        message: 'Invalid Start Date',
      };
    }

    // Check if the end date is in the future
    if (endDate && endDate > today) {
      return {
        valid: false,
        message: 'Invalid End Date',
      };
    }

    // If both dates are valid
    return {
      valid: true,
      message: "Both starting and end dates are valid.",
    };
  };




  const handleSubmit = () => {
    const formattedExperience = {
      job_title: experience.job_title,
      organization: experience.organization,
      starting: `${experience.startYear}-${String(months.indexOf(experience.startMonth) + 1).padStart(2, '0')}-01`,
      end: experience.current
        ? null
        : `${experience.endYear}-${String(months.indexOf(experience.endMonth) + 1).padStart(2, '0')}-01`,
    };
    const result = validateJobDates(formattedExperience);
    if (result.valid == false) {
      toast.warn(result.message);
      return;
    }
    onSave(formattedExperience);

    setExperience({
      job_title: '',
      organization: '',
      startMonth: '',
      startYear: '',
      endMonth: '',
      endYear: '',
      current: false
    });
    setErrors({});
  };



  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="">Add Experience</Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>List your past projects or experiences</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Job Title<Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              name="job_title"
              value={experience.job_title}
              onChange={handleChange}
              placeholder="Mortgage Broker"
              variant="outlined"
              error={errors.job_title}
              helperText={errors.job_title ? 'Job title is required' : ''}
              sx={{
                backgroundColor: 'rgba(211, 211, 211, 0.15)',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(211, 211, 211, 0.19)',
                  borderRadius: '12px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Company or Organization<Box component="span" sx={{ color: 'error.main' }}>*</Box></Typography>
            <TextField
              fullWidth
              name="organization"
              value={experience.organization}
              onChange={handleChange}
              placeholder="Doe Mortgage Solutions"
              variant="outlined"
              error={errors.organization}
              helperText={errors.organization ? 'Organization is required' : ''}
              sx={{
                backgroundColor: 'rgba(211, 211, 211, 0.15)',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(211, 211, 211, 0.19)',
                  borderRadius: '12px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={experience.current}
                  onChange={handleCheckboxChange}
                  color="primary"
                  sx={{
                    '&.Mui-checked': {
                      color: 'green', // Change the color to green when checked
                    },
                  }}
                />
              }
              label="I am currently working in this role."
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Start Date<Box component="span" sx={{ color: 'error.main' }}>*</Box></Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined" error={errors.startDate}>
                  <Select
                    name="startMonth"
                    value={experience.startMonth}
                    onChange={handleSelectChange}
                    displayEmpty
                    placeholder="Select Month"
                  >
                    <MenuItem value="" disabled>Select Month</MenuItem>
                    {months.map((month) => (<MenuItem key={`start-${month}`} value={month}>{month}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined" error={errors.startDate}>
                  <Select
                    name="startYear"
                    value={experience.startYear}
                    onChange={handleSelectChange}
                    displayEmpty
                    placeholder="Select Year"
                  >
                    <MenuItem value="" disabled>Select Year</MenuItem>
                    {years.map((year) => (
                      <MenuItem key={`start-${year}`} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            {errors.startDate && <Typography color="error">Start date must be before the end date.</Typography>}
          </Grid>

          {!experience.current && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>End Date</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined" error={errors.endDate}>
                    <Select
                      name="endMonth"
                      value={experience.endMonth}
                      onChange={handleSelectChange}
                      displayEmpty
                      placeholder="Select Month"
                    >
                      <MenuItem value="" disabled>Select Month</MenuItem>
                      {months.map((month) => (<MenuItem key={`end-${month}`} value={month}>{month}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined" error={errors.endDate}>
                    <Select
                      name="endYear"
                      value={experience.endYear}
                      onChange={handleSelectChange}
                      displayEmpty
                      placeholder="Select Year"
                    >
                      <MenuItem value="" disabled>Select Year</MenuItem>
                      {years.map((year) => (<MenuItem key={`end-${year}`} value={year}>{year}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {errors.endDate && <Typography color="error">End date must be after the start date.</Typography>}
            </Grid>
          )}

        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaveDisabled}
          sx={{
            borderRadius: 8,
            px: 4,
            backgroundColor: '#f59e0b',
            '&:hover': {
              backgroundColor: '#d97706',
            },
          }}
        >
          SAVE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExperienceModal;